/* eslint-disable @typescript-eslint/naming-convention */
import api from 'axios';
import qs from 'qs';
import { getStoredAiToken } from './aiTokenStorage';
// ---------------------------------------------------------------------------
// Orchestrator base URL — resolved once from window / env vars
// ---------------------------------------------------------------------------
let cachedOrchestratorBaseUrl = null;
export function getOrchestratorBaseUrl() {
    if (cachedOrchestratorBaseUrl)
        return cachedOrchestratorBaseUrl;
    let domain = '';
    // 1. Check window property (set by Rails view via <script> tag)
    if (typeof window !== 'undefined') {
        const win = window;
        if (win.REACT_AI_CHAT_API_GATEWAY_DOMAIN)
            domain = win.REACT_AI_CHAT_API_GATEWAY_DOMAIN;
    }
    // 2. Fallback to webpack-injected env var (REACT_* prefix only)
    if (!domain)
        domain = process.env.REACT_AI_CHAT_API_GATEWAY_DOMAIN || '';
    cachedOrchestratorBaseUrl = domain ? `${domain}/api` : '/api';
    return cachedOrchestratorBaseUrl;
}
// ---------------------------------------------------------------------------
// Orchestrator API client
// ---------------------------------------------------------------------------
// Similar to the main api-client but configured for the orchestrator:
// - Base URL resolved from window.REACT_AI_CHAT_API_GATEWAY_DOMAIN / env var
// - Authorization header injected dynamically from localStorage token
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// TosRequiredError — thrown when the orchestrator rejects with tos_required
// ---------------------------------------------------------------------------
export class TosRequiredError extends Error {
    constructor() {
        super('TOS acceptance required');
        this.name = 'TosRequiredError';
    }
}
const orchestratorApiClient = api.create({
    paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'brackets' });
    },
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
    }
});
// Inject baseURL + Authorization on every request
orchestratorApiClient.interceptors.request.use((config) => {
    // Only set baseURL when the caller hasn't provided an explicit override
    if (config.baseURL === undefined) {
        config.baseURL = getOrchestratorBaseUrl();
    }
    const token = getStoredAiToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});
// Detect tos_required denial and re-throw as a typed error so callers can
// handle TOS flow specifically without matching on HTTP status codes.
orchestratorApiClient.interceptors.response.use((response) => response, (error) => {
    var _a;
    if (api.isAxiosError(error) && ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 403) {
        const data = error.response.data;
        // eslint-disable-next-line dot-notation
        if ((data === null || data === void 0 ? void 0 : data['denial_type']) === 'tos_required') {
            return Promise.reject(new TosRequiredError());
        }
    }
    return Promise.reject(error);
});
export default orchestratorApiClient;
